import { useMatches } from "react-router";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useTheme } from "~/components/ThemeProvider";

interface Props {
  className?: string;
  type: 'yandex' | 'google';
  adId: string;
  height?: number;
}

const AdInContent: React.FC<Props> = (
  {
    className = null,
    type,
    adId,
  }) => {
  const { t } = useTranslation("translations");
  const [theme] = useTheme();

  /**
   * Only display ads if root module loader has meta.showAds == true
   */
  const matches = useMatches();
  const root = matches.find((match) => match.id === "root");
  if (!root) {
    throw new Error("Root route match was not found");
  }

  const showAds = root?.data?.meta?.showAds === true;
  const isDevMode = false;

  /**
   * Block should be rendered only after mounted,
   * because Yandex.RTB script renders some content on client only
   */
  const [isMounted, setIsMounted] = useState(false);
  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => setIsMounted(true), 1000);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (!showAds || isDevMode || !isMounted) {
      return;
    }
    switch (type) {
      case "google":
        setTimeout(() => {
          try {
            /** @ts-expect-error External */
            (adsbygoogle = window.adsbygoogle || []).push({});
            setIsRendered(true);
          } catch (e) {
            console.error("Google AdSense error: ", e);
          }
        }, 1500);

        break;
      case "yandex":
        /** @ts-expect-error External library */
        window?.yaContextCb?.push(() => {
          Ya.Context.AdvManager.render({
            "blockId": adId,
            "renderTo": `yandex_rtb_${adId}`,
            darkTheme: theme === "dark",
            onRender: () => {
              console.log(`Ad ${adId} rendered`);
              setIsRendered(true);
            },
            onError: (type, code, text) => {
              console.error(`Ad ${adId} failed to render`, type, code, text);
            }
          });
        });
        break;
    }
  }, [isMounted]);

  if (!showAds) {
    return;
  }

  let adContainer = null;
  switch (type) {
    case "google":
      adContainer = <ins className="adsbygoogle"
                         style={{"display" : "block"}}
                         data-ad-client="ca-pub-5971209848685273"
                         data-ad-slot={adId}
                         data-ad-format="auto"
                         data-full-width-responsive="true"></ins>;
      break;
    case "yandex":
      adContainer = <div id={`yandex_rtb_${adId}`} className="z-10"></div>;
      break;
  }

  return (
    <div
      className={clsx(
        className,
        "relative w-full ",
        {
          "flex items-center justify-center": isDevMode,
          "bg-slate-200 dark:bg-slate-700 rounded-lg h-[130px]": !isRendered,
          "h-auto": isRendered,
        }
      )}
    >
      {isRendered ? null : (
      <div className="text-sm text-slate-400 dark:text-slate-500 absolute top-0 left-0 w-full h-full flex justify-center items-center -z-0">
        {t("ad_placeholder")}
      </div>
      )}

      {adContainer}
    </div>
  );
};

export default AdInContent;